/**
 * Load the rent ledger (+ latest proof per row) for a set of bookings.
 *
 * One fetch path behind every past-months reader (RemindersTab,
 * AdminReminders, usePropertyReminderStats) so they page and batch the same
 * way. Supabase caps a response at 1,000 rows and a long `.in()` list at the
 * URL limit; the global reminders page asks for every tracked tenant's whole
 * rent history, so an unpaged query silently drops months once a portfolio is
 * large enough. Rows are paged with `.range()` and id lists are chunked.
 *
 * Errors throw — a missing proof page would make proof-backed months look
 * unpaid, which is worse than showing nothing.
 */
import { latestProofByPaymentId } from "./rent";
import type { RentLedgerRow } from "./rent-past-months";

export const RENT_LEDGER_COLUMNS =
  "id, booking_id, type, status, due_date, amount_cents, received_cents, stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by";

/** Supabase's default max rows per response. */
const PAGE_SIZE = 1000;
/** Keep `.in()` lists well under URL limits. */
const IN_CHUNK = 200;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size)
    out.push(items.slice(i, i + size));
  return out;
}

export interface RentLedgerWithProofs {
  ledger: RentLedgerRow[];
  proofFor: (paymentId: string) => { status: string | null } | null;
}

/**
 * `db` is the (untyped) Supabase client — the generated types lag the schema
 * for several of these columns, see usePaymentsAdmin / useProperties.
 */
export async function fetchRentLedgerForBookings(
  db: any,
  bookingIds: readonly number[]
): Promise<RentLedgerWithProofs> {
  const ledger: RentLedgerRow[] = [];
  for (const ids of chunk(bookingIds, IN_CHUNK)) {
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await db
        .from("payments")
        .select(RENT_LEDGER_COLUMNS)
        .in("booking_id", ids)
        .eq("type", "rent")
        .order("id", { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error)
        throw new Error(`Could not load rent ledger: ${error.message}`);
      const rows = (data ?? []) as RentLedgerRow[];
      ledger.push(...rows);
      if (rows.length < PAGE_SIZE) break;
    }
  }

  const proofs: Array<{ payment_id: string; status: string | null }> = [];
  for (const ids of chunk(
    ledger.map((r) => String(r.id)),
    IN_CHUNK
  )) {
    // Newest first within a chunk; ids never repeat across chunks, so
    // latestProofByPaymentId's first-seen-wins rule still holds. Paged like
    // the ledger fetch above: a heavily-proofed chunk (e.g. AdminReminders'
    // whole-portfolio fetch) can itself exceed the 1,000-row cap, and losing
    // the tail would drop exactly the proofs sorting after the cutoff —
    // silently manufacturing an unpaid/open past-month row.
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await db
        .from("payment_proofs")
        .select("payment_id, status, created_at")
        .in("payment_id", ids)
        .order("created_at", { ascending: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error)
        throw new Error(`Could not load payment proofs: ${error.message}`);
      const page = (data ?? []) as Array<{
        payment_id: string;
        status: string | null;
      }>;
      proofs.push(...page);
      if (page.length < PAGE_SIZE) break;
    }
  }
  const proofByPayment = latestProofByPaymentId(proofs);
  return {
    ledger,
    proofFor: (id) => proofByPayment.get(id) ?? null,
  };
}
